import { DateTimePicker } from "@/components/date-time-picker";
import { Field } from "@/components/form-field";
import { MotionNoiseFields } from "@/components/motion-noise-fields";
import { Input } from "@/components/ui/input";
import type { MotionNoise, PatchChallengeInput } from "@/lib/api";
import { motionNoiseInvalid } from "@/lib/api";

import { SectionCard } from "./section-card";
import { changed, useChallenge, useChallengeState } from "./use-challenge";

/** The deadline, and -- on our server -- how much of the simulator each student may use. */
export function AdminChallengeLimits() {
  const { challenge, refetch } = useChallenge();
  const onServer = challenge.simulator === "server";
  const [form, set, reset] = useChallengeState((c) => ({
    deadline: c.deadline,
    opens_at: c.opens_at,
    max_attempts: c.max_attempts === null ? "" : String(c.max_attempts),
    max_concurrent_sessions: String(c.max_concurrent_sessions),
    max_steps: String(c.max_steps),
    motion_noise: { ...c.motion_noise } as MotionNoise,
  }));

  const patch: PatchChallengeInput = {};
  if (form.deadline !== challenge.deadline) patch.deadline = form.deadline;
  if (form.opens_at !== challenge.opens_at) patch.opens_at = form.opens_at;
  if (onServer) {
    const attempts = form.max_attempts.trim() ? Number(form.max_attempts) : null;
    if (attempts !== challenge.max_attempts) patch.max_attempts = attempts;
    if (Number(form.max_concurrent_sessions) !== challenge.max_concurrent_sessions)
      patch.max_concurrent_sessions = Number(form.max_concurrent_sessions);
    if (Number(form.max_steps) !== challenge.max_steps) patch.max_steps = Number(form.max_steps);
    if (changed(form.motion_noise, challenge.motion_noise)) patch.motion_noise = form.motion_noise;
  }

  const attemptsInvalid =
    form.max_attempts.trim() !== "" &&
    (!Number.isInteger(Number(form.max_attempts)) || Number(form.max_attempts) < 1);
  const problem =
    onServer && attemptsInvalid
      ? "Attempts must be a whole number, or blank for unlimited."
      : onServer && Number(form.max_concurrent_sessions) < 1
        ? "At least one session at a time."
        : onServer && Number(form.max_steps) < 1
          ? "The step budget must be above zero."
          : onServer && motionNoiseInvalid(form.motion_noise)
            ? "Noise sigmas must be zero or positive."
            : null;

  return (
    <SectionCard
      title="Limits"
      description={
        onServer
          ? "The deadline, and how much of the simulator each student may use. All of it can change while the challenge runs; lowering a cap below what a student has spent simply blocks them."
          : "The deadline. Uploads after it are refused; the leaderboard stays readable."
      }
      patch={patch}
      problem={problem}
      onSaved={refetch}
      onReset={reset}
    >
      <Field
        id="l-opens"
        label="Opens at"
        hint="Before this, students see a countdown but get neither the exploration data nor a run. Staff keys are not held back. Leave blank to open now."
      >
        <DateTimePicker
          id="l-opens"
          value={form.opens_at}
          onChange={(opens_at) => set((f) => ({ ...f, opens_at }))}
          className="w-full sm:w-80"
        />
      </Field>

      <Field id="l-deadline" label="Deadline" hint="Leave blank for none.">
        <DateTimePicker
          id="l-deadline"
          value={form.deadline}
          onChange={(deadline) => set((f) => ({ ...f, deadline }))}
          className="w-full sm:w-80"
        />
      </Field>

      {onServer ? (
        <>
          <div className="grid gap-4 rounded-md border p-3">
            <p className="text-xs font-medium">Session limits</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                id="l-attempts"
                label={challenge.type === "competition" ? "Attempts, per team" : "Attempts, per student"}
              >
                <Input
                  id="l-attempts"
                  type="number"
                  min="1"
                  step="1"
                  value={form.max_attempts}
                  onChange={(e) => set((f) => ({ ...f, max_attempts: e.target.value }))}
                  placeholder="Unlimited"
                  aria-invalid={attemptsInvalid || undefined}
                />
              </Field>
              <Field id="l-concurrent" label="At once, per team">
                <Input
                  id="l-concurrent"
                  type="number"
                  min="1"
                  step="1"
                  value={form.max_concurrent_sessions}
                  onChange={(e) => set((f) => ({ ...f, max_concurrent_sessions: e.target.value }))}
                />
              </Field>
              <Field id="l-steps" label="Step budget">
                <Input
                  id="l-steps"
                  type="number"
                  min="1"
                  step="1000"
                  value={form.max_steps}
                  onChange={(e) => set((f) => ({ ...f, max_steps: e.target.value }))}
                />
              </Field>
            </div>
            <p className="text-muted-foreground text-xs">
              Blank attempts means unlimited; an aborted or dropped run still spends one. For
              scale, last term&apos;s median successful run took 1,490 steps and the 99th
              percentile 52,735.
            </p>
          </div>

          <MotionNoiseFields
            id="l-noise"
            value={form.motion_noise}
            onChange={(motion_noise) => set((f) => ({ ...f, motion_noise }))}
          />
        </>
      ) : null}
    </SectionCard>
  );
}
